import * as React from "react";
import {
    Badge,
    Banner,
    Button,
    Card,
    Checkbox,
    ConfirmModal,
    EmptyState,
    Field,
    IconButton,
    Input,
    ListStates,
    Modal,
    ModalBody,
    ModalFooter,
    PageContainer,
    PageHeader,
    Select,
    Textarea,
    Toolbar,
} from "../components";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import * as Structure from "../structure/StructureStore";
import * as Store from "./SubjectChoiceRulesStore";
import { describeRule } from "./subjectChoiceEvaluator";

/**
 * Subject-choice rules for one qualification + level ("NSC / Grade 10"). Every scheme a school uses - compulsory
 * subjects, either/or pairs, elective blocks, total subject or credit counts - is one rule shape: from these
 * subjects, choose between a minimum and a maximum. The presets fill that shape in so nobody has to think in
 * minimums and maximums for the common cases; Custom exposes the raw fields.
 *
 * Rules carry over every academic year. The Subject choices screen applies them, and flags any rule a particular
 * year's grade cannot meet (for example an elective block whose subjects are not offered that year).
 */

type Preset = "compulsory" | "exactlyOne" | "between" | "totalSubjects" | "totalCredits" | "custom";

const PRESETS: Array<{ key: Preset; label: string; hint: string }> = [
    { key: "compulsory", label: "Compulsory", hint: "Every chosen subject must be taken." },
    { key: "exactlyOne", label: "Choose exactly one", hint: "An either/or, such as Mathematics or Mathematical Literacy." },
    { key: "between", label: "Choose between N and M", hint: "An elective block or option line. Leave one bound blank for 'at least' or 'at most'." },
    { key: "totalSubjects", label: "Total subjects", hint: "How many subjects a student takes in all." },
    { key: "totalCredits", label: "Total credits", hint: "Credits across all subjects. Set credits on each module under Structure." },
    { key: "custom", label: "Custom", hint: "Any set of subjects, counted as subjects or credits." },
];

interface Draft {
    preset: Preset;
    name: string;
    description: string;
    appliesToAllSubjects: boolean;
    measure: string;
    minimum: string;
    maximum: string;
    moduleIDs: string[];
}

const EMPTY: Draft = {
    preset: "compulsory", name: "", description: "", appliesToAllSubjects: false, measure: "Subjects", minimum: "", maximum: "", moduleIDs: [],
};

const text = (value?: number | null) => (value === null || value === undefined ? "" : String(value));
const numberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));

/** The preset an existing rule reads as, so editing opens the same simple form it was made with. */
function presetOf(rule: Store.SubjectChoiceRule): Preset {
    const credits = rule.measure === "Credits";
    if (credits) return rule.appliesToAllSubjects ? "totalCredits" : "custom";
    if (rule.appliesToAllSubjects) return "totalSubjects";
    const count = rule.moduleIDs.length;
    if (rule.minimum === count && rule.maximum === count) return "compulsory";
    if (rule.minimum === 1 && rule.maximum === 1) return "exactlyOne";
    return "between";
}

const toDraft = (rule: Store.SubjectChoiceRule): Draft => ({
    preset: presetOf(rule),
    name: rule.name,
    description: rule.description ?? "",
    appliesToAllSubjects: rule.appliesToAllSubjects,
    measure: rule.measure,
    minimum: text(rule.minimum),
    maximum: text(rule.maximum),
    moduleIDs: rule.moduleIDs,
});

/** What the server stores for a draft: the preset decides the fields it hides. */
function toWrite(draft: Draft, qualificationID: string, qualificationLevelID: string): Store.SubjectChoiceRuleDraft {
    const base = { qualificationID, qualificationLevelID, name: draft.name.trim(), description: draft.description.trim() || undefined };
    switch (draft.preset) {
        case "compulsory":
            return { ...base, appliesToAllSubjects: false, measure: "Subjects", minimum: draft.moduleIDs.length, maximum: draft.moduleIDs.length, moduleIDs: draft.moduleIDs };
        case "exactlyOne":
            return { ...base, appliesToAllSubjects: false, measure: "Subjects", minimum: 1, maximum: 1, moduleIDs: draft.moduleIDs };
        case "between":
            return { ...base, appliesToAllSubjects: false, measure: "Subjects", minimum: numberOrNull(draft.minimum), maximum: numberOrNull(draft.maximum), moduleIDs: draft.moduleIDs };
        case "totalSubjects":
            return { ...base, appliesToAllSubjects: true, measure: "Subjects", minimum: numberOrNull(draft.minimum), maximum: numberOrNull(draft.maximum), moduleIDs: [] };
        case "totalCredits":
            return { ...base, appliesToAllSubjects: true, measure: "Credits", minimum: numberOrNull(draft.minimum), maximum: numberOrNull(draft.maximum), moduleIDs: [] };
        default:
            return {
                ...base,
                appliesToAllSubjects: draft.appliesToAllSubjects,
                measure: draft.measure,
                minimum: numberOrNull(draft.minimum),
                maximum: numberOrNull(draft.maximum),
                moduleIDs: draft.appliesToAllSubjects ? [] : draft.moduleIDs,
            };
    }
}

export const SubjectChoiceRulesScreen: React.FC = () => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.STRUCTURE);

    const qualifications = useAsync(() => Structure.listQualifications(), [], { initial: [] });
    const levels = useAsync(() => Structure.listQualificationLevels(), [], { initial: [] });
    const modules = useAsync(() => Structure.listModules(), [], { initial: [] });
    const [qualificationID, setQualificationID] = React.useState("");
    const [levelID, setLevelID] = React.useState("");
    const ready = !!qualificationID && !!levelID;
    const rules = useAsync(() => Store.listSubjectChoiceRules(qualificationID, levelID), [qualificationID, levelID], { enabled: ready });

    const moduleName = React.useCallback(
        (id: string) => (modules.data ?? []).find((m) => m.id === id)?.name ?? "(deleted subject)",
        [modules.data]
    );
    const credits = React.useCallback((id: string) => (modules.data ?? []).find((m) => m.id === id)?.credits, [modules.data]);

    const [editing, setEditing] = React.useState<Store.SubjectChoiceRule | "new" | null>(null);
    const [draft, setDraft] = React.useState<Draft>(EMPTY);
    const [deleting, setDeleting] = React.useState<Store.SubjectChoiceRule | null>(null);

    const open = (rule: Store.SubjectChoiceRule | "new") => {
        setDraft(rule === "new" ? EMPTY : toDraft(rule));
        setEditing(rule);
    };

    const save = useAction(async () => {
        const write = toWrite(draft, qualificationID, levelID);
        if (editing === "new") await Store.createSubjectChoiceRule(write);
        else if (editing) await Store.updateSubjectChoiceRule(editing.id, write);
        toast.success(editing === "new" ? "Rule added." : "Rule saved.");
        setEditing(null);
        rules.refresh();
    });

    const remove = useAction(async () => {
        if (!deleting) return;
        await Store.deleteSubjectChoiceRule(deleting.id);
        toast.success("Rule deleted.");
        setDeleting(null);
        rules.refresh();
    });

    // Optimistic: the list moves at once, and a failed save puts the server's order back.
    const [reordering, setReordering] = React.useState(false);
    const reorder = async (index: number, offset: number) => {
        const list = [...(rules.data ?? [])];
        const target = index + offset;
        if (target < 0 || target >= list.length) return;
        [list[index], list[target]] = [list[target], list[index]];
        rules.setData(list);
        setReordering(true);
        try {
            rules.setData(await Store.reorderSubjectChoiceRules(qualificationID, levelID, list.map((r) => r.id)));
        } catch {
            toast.error("The new order could not be saved.");
            rules.refresh();
        } finally {
            setReordering(false);
        }
    };

    const sortedModules = React.useMemo(
        () => [...(modules.data ?? [])].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
        [modules.data]
    );
    const preset = PRESETS.find((p) => p.key === draft.preset)!;
    const choosesSubjects = draft.preset !== "totalSubjects" && draft.preset !== "totalCredits" && !(draft.preset === "custom" && draft.appliesToAllSubjects);
    const showsBounds = draft.preset !== "compulsory" && draft.preset !== "exactlyOne";
    const draftIsCredits = draft.preset === "totalCredits" || (draft.preset === "custom" && draft.measure === "Credits");

    return (
        <PageContainer>
            <PageHeader
                title="Subject choice rules"
                icon={["fad", "scale-balanced"]}
                description="The rules a grade's subject choices must meet. They apply every year, and Subject choices will not save a student who breaks one."
                actions={
                    canWrite &&
                    ready && (
                        <Button icon={["fad", "plus"]} onClick={() => open("new")}>
                            Add rule
                        </Button>
                    )
                }
            />

            <Toolbar>
                <div className="w-full max-w-xs">
                    <label className="mb-1 block text-sm font-medium text-text">Qualification</label>
                    <Select value={qualificationID} onChange={(e) => setQualificationID(e.target.value)} placeholder="Select a qualification">
                        {(qualifications.data ?? []).map((q) => (
                            <option key={q.id} value={q.id}>
                                {q.name}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-full max-w-xs">
                    <label className="mb-1 block text-sm font-medium text-text">Level</label>
                    <Select value={levelID} onChange={(e) => setLevelID(e.target.value)} placeholder="Select a level">
                        {(levels.data ?? []).map((l) => (
                            <option key={l.id} value={l.id}>
                                {l.name}
                            </option>
                        ))}
                    </Select>
                </div>
            </Toolbar>

            {!ready ? (
                <Card>
                    <EmptyState
                        icon={["fad", "scale-balanced"]}
                        title="Pick a qualification and a level"
                        hint="Rules belong to a qualification at one level, such as the National Senior Certificate in Grade 10."
                    />
                </Card>
            ) : (
                <Card>
                    <ListStates
                        isLoading={rules.isLoading}
                        error={rules.error}
                        onRetry={rules.refresh}
                        isEmpty={(rules.data ?? []).length === 0}
                        empty={{
                            icon: ["fad", "scale-balanced"],
                            title: "No rules yet",
                            hint: "Without rules any combination of the grade's subjects can be saved. Applying a curriculum template adds its rules.",
                        }}
                    >
                        <ol className="divide-y divide-border">
                            {(rules.data ?? []).map((rule, index) => {
                                const creditsMissing =
                                    rule.measure === "Credits"
                                        ? (rule.appliesToAllSubjects ? [] : rule.moduleIDs).filter((id) => credits(id) == null).map(moduleName)
                                        : [];
                                return (
                                    <li key={rule.id} className="flex items-start gap-4 px-6 py-4">
                                        <span className="mt-0.5 w-6 shrink-0 text-right text-sm text-text-weak">{index + 1}.</span>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-medium text-text-strong">{rule.name}</span>
                                                {rule.measure === "Credits" && <Badge tone="info">Credits</Badge>}
                                                {rule.source && (
                                                    <Badge tone="neutral" title="Created by a curriculum template. Editing it is safe: a re-apply never overwrites it.">
                                                        Template
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="mt-0.5 text-sm text-text">{describeRule(rule, moduleName)}</p>
                                            {rule.description && <p className="mt-0.5 text-xs text-text-weak">{rule.description}</p>}
                                            {creditsMissing.length > 0 && (
                                                <p className="mt-1 text-xs text-warning-text">
                                                    {creditsMissing.join(", ")} {creditsMissing.length === 1 ? "has" : "have"} no credits and count as 0.
                                                </p>
                                            )}
                                        </div>
                                        {canWrite && (
                                            <div className="flex shrink-0 gap-1">
                                                <IconButton icon={["fad", "arrow-up"]} label="Move up" disabled={index === 0 || reordering} onClick={() => reorder(index, -1)} />
                                                <IconButton
                                                    icon={["fad", "arrow-down"]}
                                                    label="Move down"
                                                    disabled={index === (rules.data ?? []).length - 1 || reordering}
                                                    onClick={() => reorder(index, 1)}
                                                />
                                                <IconButton icon={["fad", "pen"]} label="Edit" onClick={() => open(rule)} />
                                                <IconButton icon={["fad", "trash"]} label="Delete" variant="outline-danger" onClick={() => setDeleting(rule)} />
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </ListStates>
                </Card>
            )}

            <Modal
                isOpen={editing !== null}
                onClose={() => setEditing(null)}
                title={editing === "new" ? "Add rule" : "Edit rule"}
                icon={["fad", "scale-balanced"]}
                size="lg"
            >
                <ModalBody>
                    {save.error && (
                        <Banner tone="danger" className="mb-4">
                            {save.error}
                        </Banner>
                    )}
                    <div className="grid gap-4">
                        <Field label="Kind of rule" hint={preset.hint}>
                            <Select value={draft.preset} onChange={(e) => setDraft({ ...draft, preset: e.target.value as Preset })}>
                                {PRESETS.map((p) => (
                                    <option key={p.key} value={p.key}>
                                        {p.label}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label="Name" required hint="Shown on the subject-choice grid and in every message about this rule.">
                            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Electives" />
                        </Field>

                        {draft.preset === "custom" && (
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label="Count">
                                    <Select value={draft.measure} onChange={(e) => setDraft({ ...draft, measure: e.target.value })}>
                                        <option value="Subjects">Subjects</option>
                                        <option value="Credits">Credits</option>
                                    </Select>
                                </Field>
                                <div className="pt-7">
                                    <Checkbox
                                        label="Applies to every subject"
                                        checked={draft.appliesToAllSubjects}
                                        onChange={(e) => setDraft({ ...draft, appliesToAllSubjects: e.target.checked })}
                                    />
                                </div>
                            </div>
                        )}

                        {showsBounds && (
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label={draftIsCredits ? "Minimum credits" : "Minimum"} hint="Blank for no minimum.">
                                    <Input type="number" min={0} value={draft.minimum} onChange={(e) => setDraft({ ...draft, minimum: e.target.value })} />
                                </Field>
                                <Field label={draftIsCredits ? "Maximum credits" : "Maximum"} hint="Blank for no maximum.">
                                    <Input type="number" min={0} value={draft.maximum} onChange={(e) => setDraft({ ...draft, maximum: e.target.value })} />
                                </Field>
                            </div>
                        )}

                        {choosesSubjects && (
                            <Field label="Subjects" required hint="Subjects a grade does not offer in a given year are simply not counted.">
                                <div className="grid max-h-64 gap-1 overflow-y-auto rounded-md border border-border p-3 sm:grid-cols-2">
                                    {sortedModules.map((m) => (
                                        <Checkbox
                                            key={m.id}
                                            label={m.credits != null ? `${m.name} (${m.credits} credits)` : m.name}
                                            checked={draft.moduleIDs.includes(m.id ?? "")}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    moduleIDs: e.target.checked
                                                        ? [...draft.moduleIDs, m.id ?? ""]
                                                        : draft.moduleIDs.filter((id) => id !== m.id),
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </Field>
                        )}

                        <Field label="Description">
                            <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                        </Field>

                        {draft.name.trim() && (
                            <Banner tone="info">{describeRule({ id: "", ...toWrite(draft, "", "") }, moduleName)}</Banner>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline-secondary" onClick={() => setEditing(null)}>
                        Cancel
                    </Button>
                    <Button
                        icon={["fad", "floppy-disk"]}
                        onClick={save.run}
                        loading={save.busy}
                        disabled={!draft.name.trim() || (choosesSubjects && draft.moduleIDs.length === 0)}
                    >
                        Save rule
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmModal
                isOpen={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={remove.run}
                title="Delete rule"
                icon={["fad", "trash"]}
                body={<>Delete the rule &ldquo;{deleting?.name}&rdquo;? Subject choices stop checking it straight away.</>}
                confirmLabel="Delete"
                confirmVariant="danger"
                busy={remove.busy}
                error={remove.error}
            />
        </PageContainer>
    );
};

export default SubjectChoiceRulesScreen;
